import { Button, Modal, Text, Stack, Group, Checkbox, Box, Container, Title, List } from '@mantine/core';
import React, { useState } from 'react';
import { useAuthContext } from '@/context/Auth';
import { useResponsive } from '@/hooks/use-responsive';

interface PrivacyPolicyPopupProps {
  opened: boolean;
  onClose: () => void;
  onAccept?: () => void;
}

const PrivacyPolicyPopup: React.FC<PrivacyPolicyPopupProps> = ({ opened, onClose, onAccept }) => {
  const { dispatch } = useAuthContext();
  const [isChecked, setIsChecked] = useState(false);
  const { responsive } = useResponsive();

  const handleAccept = async () => {
    if (!isChecked) return;
    dispatch({ type: 'PRIVACY_POLICY_ACCEPTED' });
    onClose();
    onAccept?.();
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {}} // Prevent closing by clicking outside or pressing escape
      title="Privacy Policy"
      size="xl"
      centered
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Box
        component="main"
        py={responsive({
          xs: 20,
          sm: 30,
          md: 40
        })}
      >
        <Container size="xl">
          <Stack gap={responsive({
            xs: 'sm',
            sm: 'md',
            md: 'lg'
          })}>
            <Text fz={responsive({
              xs: 14,
              sm: 15,
              md: 16
            })}>
              Home Health Solutions is committed to safeguarding the personal information entrusted to us by our clients.
              We manage your personal information in accordance with Alberta&apos;s Personal Information Protection Act and other applicable laws.
              This policy outlines the principles and practices we follow in protecting your personal information.
            </Text>

            <Box>
              <Text
                fw={600}
                mb="xs"
                fz={responsive({
                  xs: 15,
                  sm: 16,
                  md: 18
                })}
              >
                Personal Information
              </Text>
              <Text fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                Personal information means information about an identifiable individual. This includes an individual&apos;s name,
                home address and phone number, age, sex, marital or family status, an identifying number, financial information,
                educational history, etc.
              </Text>
            </Box>

            <Box>
              <Text
                fw={600}
                mb="xs"
                fz={responsive({
                  xs: 15,
                  sm: 16,
                  md: 18
                })}
              >
                Information We Collect
              </Text>
              <Text fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We collect only the personal information that we need for the purposes of providing services to our clients,
                including personal information needed to:
              </Text>
              <List
                mt="xs"
                spacing="xs"
                fz={responsive({
                  xs: 14,
                  sm: 15,
                  md: 16
                })}
              >
                <List.Item>Open and manage an account</List.Item>
                <List.Item>Communicate important updates, responses, and notifications to clients</List.Item>
                <List.Item>Verify the identity of clients to ensure secure access to services</List.Item>
                <List.Item>Contact clients about appointments</List.Item>
                <List.Item>Manage client accounts, including billing and customer support</List.Item>
                <List.Item>Deliver requested services</List.Item>
                <List.Item>Assess suitability of a property</List.Item>
                <List.Item>Meet regulatory or bylaw requirements</List.Item>
                <List.Item>Send promotional materials and information about new services, with client consent</List.Item>
              </List>
              <Text mt="md" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We normally collect client information directly from our clients. We may collect your information from other persons with your consent or as authorized by law.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We inform our clients, before or at the time of collecting personal information, of the purposes for which we are collecting the information. However, we don&apos;t provide this notification when a client volunteers information for an obvious purpose (for example, producing a credit card for a payment when the information will be used only to process the payment).
              </Text>
            </Box>

            <Box>
              <Text
                fw={600}
                mb="xs"
                fz={responsive({
                  xs: 15,
                  sm: 16,
                  md: 18
                })}
              >
                Consent
              </Text>
              <Text fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We ask for consent to collect, use or disclose client personal information, except in specific circumstances
                where collection, use or disclosure without consent is authorized or required by law. We may assume your
                consent in cases where you volunteer information for an obvious purpose.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We ask for your express consent for some purposes and may not be able to provide certain services if you are unwilling to provide consent to the collection, use or disclosure of certain personal information. Where express consent is needed, we will normally ask clients to provide their consent orally (in person, by telephone), in writing (by signing a consent form, by checking a box on a form, or electronically by clicking a button).
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                In cases that do not involve sensitive personal information, we may rely on &quot;opt-out&quot; consent. For example, we may disclose your contact information to other organizations that we believe may be of interest to you, unless you request that we do not disclose your information. You can do this by telephoning our local number/toll-free number or emailing us.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                A client may withdraw consent to the use and disclosure of personal information at any time, unless the personal information is necessary for us to fulfil our legal obligations. We will respect your decision, but we may not be able to provide you with certain services if we do not have the necessary personal information.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We may collect, use or disclose client personal information without consent only as authorized by Alberta&apos;s Personal Information Protection Act. For example, we may not request consent when the collection, use or disclosure is reasonable for an investigation or legal proceeding, to collect a debt owed to our organization, in an emergency that threatens life, health or safety, or when the personal information is from a public telephone directory.
              </Text>
            </Box>

            <Box>
              <Text
                fw={600}
                mb="xs"
                fz={responsive({
                  xs: 15,
                  sm: 16,
                  md: 18
                })}
              >
                Use and Disclosure of Personal Information
              </Text>
              <Text fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We use and disclose client personal information only for the purposes for which the information was collected, except as authorized by law. For example, we may use client contact information to provide our services. The law also allows us to use that contact information for the purpose of collecting a debt owed to our organization, should that be necessary.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                If we wish to use or disclose your personal information for any new business purpose, we will ask for your consent.
              </Text>
            </Box>

            <Box>
              <Text
                fw={600}
                mb="xs"
                fz={responsive({
                  xs: 15,
                  sm: 16,
                  md: 18
                })}
              >
                Safeguarding Personal Information
              </Text>
              <Text fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We make every reasonable effort to ensure that client information is accurate and complete. Our safeguarding measures include:
              </Text>
              <List
                mt="xs"
                spacing="xs"
                fz={responsive({
                  xs: 14,
                  sm: 15,
                  md: 16
                })}
              >
                <List.Item>Encrypting data both in transit and at rest to prevent unauthorized access</List.Item>
                <List.Item>Regularly updating security protocols to defend against emerging threats</List.Item>
                <List.Item>Implementing strong access controls for authorized personnel only</List.Item>
                <List.Item>Using secure authentication methods, including two-factor authentication</List.Item>
                <List.Item>Conducting regular security audits and assessments</List.Item>
                <List.Item>Training employees on data privacy practices</List.Item>
                <List.Item>Establishing a robust incident response plan</List.Item>
                <List.Item>Ensuring third-party vendors adhere to strict privacy standards</List.Item>
                <List.Item>Anonymizing or pseudonymizing data where possible</List.Item>
                <List.Item>Providing clients with clear options to control their data</List.Item>
              </List>
              <Text mt="md" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We rely on our clients to notify us if there is a change to their personal information that may affect their relationship with our organization. If you are aware of an error in our information about you, please let us know and we will correct it on request wherever possible. In some cases we may ask for a written request for correction.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We protect client personal information in a manner appropriate for the sensitivity of the information. We make every reasonable effort to prevent any loss, misuse, disclosure or modification of personal information, as well as any unauthorized access to personal information.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We will notify the Office of the Information and Privacy Commissioner of Alberta, without delay, of a security breach affecting personal information if it creates a real risk of significant harm to individuals.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We retain client personal information only as long as is reasonable to fulfil the purposes for which the information was collected or for legal or business purposes.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We render client personal information non-identifying, or destroy records containing personal information once the information is no longer needed. We use appropriate security measures when destroying client personal information, including shredding paper records and permanently deleting electronic records.
              </Text>
            </Box>

            <Box>
              <Text
                fw={600}
                mb="xs"
                fz={responsive({
                  xs: 15,
                  sm: 16,
                  md: 18
                })}
              >
                Access to Records Containing Personal Information
              </Text>
              <Text fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                Clients of Home Health Solutions have a right of access to their own personal information in a record that is in our custody or under our control, subject to some exceptions. For example, organizations are required under the Personal Information Protection Act to refuse to provide access to information that would reveal personal information about another individual.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                Organizations are authorized under the Act to refuse access to personal information if disclosure reveals confidential business information. Access may also be refused if the information is privileged or contained in mediation records.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                If we refuse a request in whole or in part, we will provide the reasons for the refusal. In some cases where exceptions apply to access, we may withhold that information and provide you with the remainder of the record.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                You may make a request for access to your personal information by writing to Drew Spencer, Founder of Home Health Solutions. You must provide sufficient information in your request to allow us to identify the information you are seeking.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                You may also request information about our use of your personal information and any disclosure of that information to people outside our organization. For personal information collected before January 2004, if we do not have a record of disclosures, we will provide information about any disclosure of your information that is likely to have occurred.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                You may also request a correction of an error or omission in your personal information.
              </Text>
              <Text mt="xs" fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                We will respond to your request within 45 calendar days, unless an extension is granted. We may charge a reasonable fee to provide information, but not to make corrections. We will advise you of any fees that may apply before beginning to process your request.
              </Text>
            </Box>

            <Box>
              <Text
                fw={600}
                mb="xs"
                fz={responsive({
                  xs: 15,
                  sm: 16,
                  md: 18
                })}
              >
                Questions and Complaints
              </Text>
              <Text fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}>
                If you have a question or concern about any collection, use or disclosure of personal information by
                Home Health Solutions, or about a request for access to your own personal information, please contact:
              </Text>
              <List
                mt="xs"
                spacing="xs"
                fz={responsive({
                  xs: 14,
                  sm: 15,
                  md: 16
                })}
              >
                <List.Item>Name: Drew Spencer</List.Item>
                <List.Item>Position: Founder</List.Item>
                <List.Item>Organization: <a href="https://www.homehealthsolutions.io" target="_blank" rel="noopener noreferrer">Home Health Solutions</a></List.Item>
                <List.Item>Email: <a href="mailto:drew.s@homehealthsolutions.io" style={{ textDecoration: 'underline', color: 'inherit' }}>drew.s@homehealthsolutions.io</a></List.Item>
              </List>
            </Box>

            <Text
              ta="center"
              fw={500}
              mt={responsive({
                xs: 20,
                sm: 30,
                md: 40
              })}
              mb={responsive({
                xs: 10,
                sm: 15,
                md: 20
              })}
              fz={responsive({
                xs: 14,
                sm: 15,
                md: 16
              })}
            >
              © <a href="https://www.homehealthsolutions.io" target="_blank" rel="noopener noreferrer">HomeHealth Solutions Inc.</a>, {new Date().getFullYear()}, Calgary, Alberta.
            </Text>

            <Checkbox
              label="I have read and agree to the Privacy Policy"
              checked={isChecked}
              onChange={(event) => setIsChecked(event.currentTarget.checked)}
              mt="md"
            />
            
            <Group justify="center" mt="md">
              <Button
                onClick={handleAccept}
                size="lg"
                disabled={!isChecked}
              >
                Accept Privacy Policy
              </Button>
            </Group>
          </Stack>
        </Container>
      </Box>
    </Modal>
  );
};

export default PrivacyPolicyPopup; 